// "Current view" for a run that takes a region: the viewport's visible
// rectangle in base-level image pixels, origin top-left (the engine's
// viewportToImage, so a rotated view gives the rotated rectangle it shows).

import type { ViewportEngine } from '../../viewer/ViewportEngine';
import type { Action, RunRegion } from './types';

const regionInputs = (action: Pick<Action, 'inputs' | 'input'>) =>
  Object.values(action.inputs ?? action.input ?? {}).filter((i) => i?.type === 'region');

/** Whether an action's inputs include a region. */
export function takesRegion(action: Pick<Action, 'inputs' | 'input'>): boolean {
  return regionInputs(action).length > 0;
}

/** Whether every run of it needs one: the server refuses a run without. */
export function needsRegion(action: Pick<Action, 'inputs' | 'input'>): boolean {
  return regionInputs(action).some((i) => i.required === true);
}

type Corners = Pick<ViewportEngine, 'viewportToImage'> & Partial<Pick<ViewportEngine, 'getViewportSize'>>;

/** The four corners of what is on screen, clockwise from the top left; null when the engine cannot say. */
export function currentViewRegion(engine: Corners | null | undefined, fallbackSize?: { w: number; h: number }): RunRegion | null {
  if (!engine) return null;
  const size = engine.getViewportSize?.() ?? fallbackSize ?? null;
  if (!size || size.w <= 0 || size.h <= 0) return null;
  const corners: Array<[number, number]> = [
    [0, 0],
    [size.w, 0],
    [size.w, size.h],
    [0, size.h],
  ];
  const polygon: Array<[number, number]> = [];
  for (const [x, y] of corners) {
    const p = engine.viewportToImage(x, y);
    if (!p || !Number.isFinite(p.x) || !Number.isFinite(p.y)) return null;
    polygon.push([Math.max(0, Math.round(p.x)), Math.max(0, Math.round(p.y))]);
  }
  return { polygon };
}
